import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useNotificaciones } from "../../context/NotificacionContext";
import { useSearchParams } from "react-router-dom";
import UsersSection from "../../components/admin/UserSection";
import ControlHorarios from "../admin/ControlHorarios";
import EmpresasSection from "../../components/admin/EmpresasSection";
import CreateUserPage from "../../components/forms/CreateUserPage";
import ValidacionFichajesSection from "../../components/admin/ValidacionFichajesSection";
import { BACKEND_URL } from "../../api/api";
import logoRosoco from "../../assets/images/RoscoTransparente.gif";

/**
 * Panel principal para administradores globales.
 * Permite gestionar todas las empresas del sistema, crear usuarios globales,
 * ver fichajes de todos los empleados y acceder a funcionalidades administrativas completas.
 * 
 * @returns {JSX.Element} Dashboard del administrador global
 */
export default function DashboardGlobalAdmin() {
  const { logout, user } = useContext(AuthContext);
  const { validacionesPendientes } = useNotificaciones();
  const [searchParams, setSearchParams] = useSearchParams();
  const { darkMode, toggleDarkMode } = useTheme();
  const [section, setSection] = useState("empresas");
  const [sidebarOpen, setSidebarOpen] = useState(false); // Cerrado por defecto en mobile
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);

  // Leer parámetro de usuario de la URL
  useEffect(() => {
    const userId = searchParams.get('usuario');
    if (userId) {
      setSelectedUserId(userId);
      setSection('fichajes');
    }
  }, [searchParams]);

  // Cerrar sidebar al cambiar de sección en mobile
  useEffect(() => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, [section]);

  const handleLogout = () => {
    logout();
    setShowLogoutModal(false);
  };

  const menuItems = [
    {
      id: "empresas",
      label: "Empresas",
      icon: "fa-building",
      submenu: [
        { id: "empresas", label: "Gestión de Empresas", icon: "fa-building" }
      ]
    },
    {
      id: "usuarios",
      label: "Usuarios",
      icon: "fa-users",
      submenu: [
        { id: "crear-usuario", label: "Crear Usuario", icon: "fa-user-plus" },
        { id: "lista-usuarios", label: "Lista de Usuarios", icon: "fa-list" }
      ]
    },
    {
      id: "fichajes",
      label: "Fichajes",
      icon: "fa-clipboard-list",
      submenu: [
        { id: "fichajes", label: "Control de Horarios", icon: "fa-calendar-week" },
        { id: "validacion-fichajes", label: "Validación Pendiente", icon: "fa-clock" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Superior */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 md:px-6 py-4">
          {/* Logo y Toggle */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-700 hover:text-gray-900 transition-colors"
            >
              <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'} text-2xl`}></i>
            </button>
            <div className="flex items-center gap-3">
              <img 
                src={logoRosoco} 
                alt="Logo" 
                className="h-10 w-auto object-contain"
              />
              <div className="hidden md:block">
                <h1 className="text-xl font-bold text-gray-800">Panel de Administración Global</h1>
                <p className="text-sm text-gray-500">Gestión de todas las empresas</p>
              </div>
            </div>
          </div>

          {/* Usuario y Logout */}
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right">
              <p className="text-sm font-semibold text-gray-800">{user?.nombre}</p>
              <p className="text-xs text-gray-500">Administrador Global</p>
            </div>
            {user?.imagenPerfil && (
              <img
                src={`${BACKEND_URL}${user.imagenPerfil}`}
                alt={user.nombre}
                className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
              />
            )}
            <button
              onClick={toggleDarkMode}
              title={darkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition text-gray-600 dark:text-gray-200"
            >
              {darkMode ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 7a5 5 0 100 10A5 5 0 0012 7z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                </svg>
              )}
            </button>
            <button
              onClick={() => setShowLogoutModal(true)}
              className="hidden md:flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2 rounded-lg transition font-medium"
            >
              <i className="fas fa-sign-out-alt"></i>
              <span>Salir</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed top-[73px] left-0 h-[calc(100vh-73px)] bg-white border-r border-gray-200 transition-transform duration-300 z-30 w-72 overflow-y-auto ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => (
              <div key={item.id}>
                <div className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  <i className={`fas ${item.icon} mr-2`}></i>
                  {item.label}
                </div>
                <div className="space-y-1">
                  {item.submenu.map((subItem) => (
                    <button
                      key={subItem.id}
                      onClick={() => {
                        setSection(subItem.id);
                        setUserToEdit(null);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-lg transition-all ${
                        section === subItem.id
                          ? 'bg-gray-800 text-white shadow-md'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                        section === subItem.id
                          ? 'bg-gray-700'
                          : 'bg-gray-100'
                      }`}>
                        <i className={`fas ${subItem.icon} text-base`}></i>
                      </div>
                      <span className="font-medium text-sm">{subItem.label}</span>
                      {subItem.id === "validacion-fichajes" && validacionesPendientes > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                          {validacionesPendientes}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Footer del Sidebar */}
          <div className="p-4 mt-auto border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              <div className="mb-1">Desarrollado por</div>
              <a 
                href="https://www.faecagranada.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-800 font-semibold transition flex items-center justify-center gap-1"
              >
                FAECA Granada
                <i className="fas fa-external-link-alt text-xs"></i>
              </a>
            </div>
          </div>
        </aside>

        {/* Overlay para cerrar sidebar en mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        {/* Contenido Principal */}
        <main className={`flex-1 p-4 md:p-6 transition-all duration-300 overflow-hidden ${
          sidebarOpen ? 'md:ml-72' : 'ml-0'
        }`}>
          {section === "empresas" && (
            <EmpresasSection 
              onSelectEmpresa={(empresaId) => {
                setSelectedEmpresaId(empresaId);
                setSection("lista-usuarios");
              }}
            />
          )}
          {section === "crear-usuario" && <CreateUserPage />}
          {section === "lista-usuarios" && (
            <UsersSection 
              empresaId={selectedEmpresaId}
              onEditUser={(user) => {
                setUserToEdit(user);
                setSection("editar-usuario");
              }}
              onSelectUser={(userId) => {
                setSelectedUserId(userId);
                setSection('fichajes');
                setSearchParams({ usuario: userId });
              }}
              onClearEmpresaFilter={() => setSelectedEmpresaId(null)}
            />
          )}
          {section === "editar-usuario" && userToEdit && (
            <CreateUserPage
              user={userToEdit}
              onBack={() => {
                setSection("lista-usuarios");
                setUserToEdit(null);
              }}
              onSaved={() => {
                setSection("lista-usuarios");
                setUserToEdit(null);
              }}
            />
          )}
          {section === "fichajes" && <ControlHorarios />}
          {section === "validacion-fichajes" && <ValidacionFichajesSection />}
        </main>
      </div>

      {/* Modal de confirmación de logout */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-md transform transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <i className="fas fa-sign-out-alt text-red-600 text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800">
                Cerrar sesión
              </h3>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed">
              ¿Estás seguro de que quieres salir? Tu sesión actual se cerrará.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 px-5 py-3 rounded-lg hover:bg-gray-200 transition font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 bg-red-600 text-white px-5 py-3 rounded-lg hover:bg-red-700 transition font-medium flex items-center justify-center gap-2 shadow-md"
              >
                <i className="fas fa-sign-out-alt"></i>
                Salir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Botón flotante de logout en mobile */}
      <button
        onClick={() => setShowLogoutModal(true)}
        className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-red-600 text-white rounded-full shadow-2xl flex items-center justify-center z-30 hover:scale-110 transition-transform"
      >
        <i className="fas fa-sign-out-alt text-xl"></i>
      </button>
    </div>
  );
}
